/**
 * Service layer — Threads domain
 *
 * Owns: Threads (tblXlbRZkUldgBHkc),
 *       Thread Activity (tblrb79TQB7KlwvdL)
 *
 * NOT here: who is working the thread. RASCI was retired 2026-09-02
 * (docs/FLOW_RECONCILIATION_PLAN.md) — a thread is crewed on the Flight Board
 * (thread crews / squadrons, services/flights.js) or it has no owner at all.
 */

import { apiGet, apiGetOne, apiPost, apiPatch, apiDelete, apiPostBatch, apiPatchBatch } from './_api.js'
import { getSubmittalByThread, deleteSubmittal } from './submittals.js'
import { getRfiByThread, deleteRfi } from './rfis.js'
import { notifyThreadFollowers, claimThreadNumbers } from './passengers.js'
import { ctx } from '../outline/ctx.js'

// Who is acting, for follower updates (the outline's resolved member; blank outside the app shell).
function actorName() {
  try { const f = (ctx.currentMember && ctx.currentMember.fields) || {}; return f['Member Name'] || f['Full Name'] || f['Name'] || '' } catch { return '' }
}

const TBL_THREADS    = 'tblXlbRZkUldgBHkc'
const TBL_THREAD_ACT = 'tblrb79TQB7KlwvdL'

function todayStr() { return new Date().toISOString().slice(0, 10) }

/**
 * Fetch all Threads for a project, sorted by Last Activity desc.
 * No fields[] restriction — all fields needed for status formula, deadline, etc.
 */
export function getProjectThreads(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGet(
    TBL_THREADS,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Last+Activity&sort%5B0%5D%5Bdirection%5D=desc&maxRecords=200'
  )
}

/**
 * Create a new Thread record.
 * @param {string} projectId — Project record ID
 * @param {string} title — Thread title
 * @param {string} type — Thread Type (singleSelect value)
 * @param {null} _targetDeadline — kept for call-site shape; threads carry no date since 2026-09-10
 * @param {string|null} description — Optional description text
 * @param {string} memberId — Member record ID of creator
 * @param {object} [opts] — { phaseId } phase attachment. A thread attaches to
 *   ONE phase ('Phase' single-preference link on Threads); phase-linked
 *   threads mount under Schedule > Phase > Threads, unlinked threads appear
 *   only in Reference > Threads.
 */
export async function createThread(projectId, title, type, _targetDeadline, description, memberId, opts = {}) {
  const fields = {
    'Title':   title,
    'Type':    type,
    'Project': [projectId],
    'Last Activity': todayStr(),
  }
  if (description)    fields['Description']     = description
  if (memberId)       fields['Created By']      = [memberId]
  if (opts.phaseId)   fields['Phase']           = [opts.phaseId]
  // Its number from the one counter (database/331), so two threads never share one. A failed claim
  // never blocks creating the thread.
  try { const [n] = await claimThreadNumbers(projectId, 1); if (n) fields['Thread Number'] = n } catch (_) { /* unnumbered */ }
  const rec = await apiPost(TBL_THREADS, fields)
  // Lifecycle: every new thread opens with a 'Thread Opened' activity — this
  // drives Status (🟢 Open), the Kanban inclusion formula (requires a Thread
  // Opened activity). (The Airtable stale clock still ticks but nothing reads it — see threadStatus.)
  // Non-fatal: the thread is still created if the activity write fails.
  if (rec && rec.id) {
    // Record the thread's description as the opening activity's notes so it's visible in the outline
    // activity log. Kept in sync with the thread Description (see updateThreadActivity / updateThread).
    try { await createThreadActivity(rec.id, 'Thread Opened', todayStr(), description || null, memberId) } catch (_) { /* non-fatal */ }
  }
  return rec
}

/**
 * Create many threads at once, 10 per request (the meeting-notes import; Matt 2026-09-23: commit
 * wrote one record at a time and took minutes). Same field set as createThread, but the opening
 * 'Thread Opened' activity is NOT created here — the caller writes every activity in one batch
 * (createThreadActivitiesBatch) so it can date them. Each spec: { projectId, title, type,
 * description?, memberId?, phaseId?, lastActivity?, extraFields? }. Returns the created records
 * in the same order as the specs. Throws if a batch fails.
 */
export async function createThreadsBatch(specs) {
  // Numbers first, one claim per project for the whole batch (database/331) — the old automation
  // gave every thread in a meeting import the same number.
  const numbers = new Map()
  const perProject = new Map()
  ;(specs || []).forEach((s, i) => { if (!perProject.has(s.projectId)) perProject.set(s.projectId, []); perProject.get(s.projectId).push(i) })
  for (const [pid, idxs] of perProject) {
    try { const ns = await claimThreadNumbers(pid, idxs.length); idxs.forEach((i, k) => { if (ns[k]) numbers.set(i, ns[k]) }) } catch (_) { /* unnumbered */ }
  }
  const records = (specs || []).map((s, i) => {
    const f = { 'Title': s.title, 'Type': s.type, 'Project': [s.projectId], 'Last Activity': s.lastActivity || todayStr() }
    if (numbers.has(i)) f['Thread Number'] = numbers.get(i)
    if (s.description) f['Description'] = s.description
    if (s.memberId)    f['Created By']  = [s.memberId]
    if (s.phaseId)     f['Phase']       = [s.phaseId]
    return { ...f, ...(s.extraFields || {}) }
  })
  return records.length ? apiPostBatch(TBL_THREADS, records) : Promise.resolve([])
}

/** Patch many threads at once. patches: [{ id, fields }]. Returns { succeeded: id[], failed: [{id,error}] }. */
export function updateThreadsBatch(patches) {
  return (patches && patches.length) ? apiPatchBatch(TBL_THREADS, patches) : Promise.resolve({ succeeded: [], failed: [] })
}

/**
 * Patch fields on an existing Thread.
 */
export function updateThread(threadId, fields) {
  return apiPatch(TBL_THREADS, threadId, fields)
}

/**
 * Fully delete a thread and everything attached to it (after the caller confirms):
 *   1. The submittal OR RFI this thread directly tracks (thread_airtable_id match) — and, for a
 *      submittal, its revisions + revision files + Storage objects (handled by deleteSubmittal's
 *      FK cascade + storage cleanup). RFIs have no children.
 *   2. All Thread Activity records on the thread.
 *   3. The thread record itself.
 * Each step is best-effort so a partial failure still removes as much as possible. The thread
 * itself is deleted last and its result is returned.
 */
export async function deleteThreadCascade(threadId) {
  if (!threadId) return { error: 'No thread id' }

  // 1. Linked tracking record (Supabase). A thread tracks at most one of these.
  // An RFI/Submittal that lives on a MILESTONE (2026-09-16 model) is never deleted with an old thread
  // (Matt, 2026-09-24): the milestone owns it now.
  try { const sub = await getSubmittalByThread(threadId); if (sub && sub.id && !sub.milestone_airtable_id) await deleteSubmittal(sub.id) } catch (_) { /* non-fatal */ }
  try { const rfi = await getRfiByThread(threadId);       if (rfi && rfi.id && !rfi.milestone_airtable_id) await deleteRfi(rfi.id) }       catch (_) { /* non-fatal */ }

  // 2. Thread activities
  try {
    const acts = await getThreadActivities(threadId)
    for (const a of (acts || [])) { try { await apiDelete(TBL_THREAD_ACT, a.id) } catch (_) { /* skip */ } }
  } catch (_) { /* non-fatal */ }

  // 3. The thread itself
  return apiDelete(TBL_THREADS, threadId)
}

/* ── Thread categories (Round 7) ─────────────────────────────────────────
 * Client-side mirror of Airtable's 'Thread Order' formula so category branch
 * nodes render in the firm's canonical order. Types missing from the formula
 * (Specialty Design, Tendering Items) slot near their domain; unknowns last.
 */
export const THREAD_TYPE_ORDER = {
  'Schedule and Progress':  1,
  'Design Approvals':       2,
  'Submittals':             2,
  'Design Direction':       3,
  'RFIs':                   3,
  'Specialty Design':       4,
  'Engineering Items':      4,
  'PCOs and Change Orders': 4,
  'Permitting Items':       5,
  'Contractor Items':       5,
  'Inspector Items':        6,
  'Owner Items':            7,
  'Architect Items':        8,
  'Tendering Items':        8,
  'Misc. Items':            9,
}

/**
 * The status a thread SHOWS. Airtable's Status formula still knows "🟡 Stale" (no activity in 5
 * days) and "⏰ OVERDUE" (a past Target Deadline); both concepts are retired (Matt, 2026-09-10 —
 * docs/PASSENGERS.md): a thread has no date of its own, and a quiet thread riding a milestone a
 * ways out is quiet on purpose. Daily triage is what keeps threads moving, not a clock. So a
 * thread is Open, Closed, or STUCK (the one flag a person sets) — nothing else.
 */
export function threadStatus(f) {
  const raw = String((f && f['Status']) || '')
  if (raw.includes('STUCK')) return raw
  if ((f && f['Closed'] === true) || raw.includes('Closed')) return '⚪ Closed'
  return raw ? '🟢 Open' : ''
}

/**
 * Open / closed tallies for a thread list — drives the count meta on Threads branch nodes and
 * category nodes. Closed = the writable Closed checkbox (or Status fallback). There is no overdue
 * count any more: threads carry no target date (2026-09-10); lateness belongs to the plane a
 * thread rides.
 */
export function threadCounts(threads) {
  let open = 0, closed = 0
  ;(threads || []).forEach(t => {
    const f = t.fields || {}
    const isClosed = f['Closed'] === true || (f['Status'] || '').includes('Closed')
    if (isClosed) closed++
    else open++
  })
  return { open, closed }
}

/**
 * Group threads by Type into ordered { type, threads } buckets.
 * Untyped threads land in 'Misc. Items'.
 */
export function groupThreadsByType(threads) {
  const groups = {}
  const seen = []
  ;(threads || []).forEach(t => {
    const type = t.fields['Type'] || 'Misc. Items'
    if (!groups[type]) { groups[type] = []; seen.push(type) }
    groups[type].push(t)
  })
  seen.sort((a, b) => {
    const oa = THREAD_TYPE_ORDER[a] ?? 99
    const ob = THREAD_TYPE_ORDER[b] ?? 99
    return oa !== ob ? oa - ob : (a < b ? -1 : 1)
  })
  return seen.map(type => ({ type, threads: groups[type] }))
}

/* ── Phase attachment (Round 7 — replaces the milestone-linkage model) ──── */

/**
 * Threads attached to a phase via the 'Phase' single-preference record link.
 * multipleRecordLinks return real recXXX IDs via the API, so the match is a
 * direct ID comparison. Threads with no Phase appear only in
 * Reference > Threads. Sorted oldest first by Created Date (threads carry no date of their own).
 *
 * @param {Array} threads — raw Thread records
 * @param {object} phase — Phase record
 */
export function threadsForPhase(threads, phase) {
  if (!phase) return []
  return (threads || [])
    .filter(t => (t.fields['Phase'] || [])[0] === phase.id)
    .sort((a, b) => {
      const ad = a.fields['Created Date'] || ''
      const bd = b.fields['Created Date'] || ''
      return ad < bd ? -1 : ad > bd ? 1 : 0
    })
}

/**
 * Attach a file to a Thread's native Airtable Attachments field. Stages the upload in Supabase
 * Storage (api/upload.js's threadAttachmentUploadUrl action) to get a fetchable URL, then PATCHes
 * Attachments with the existing attachments (kept by their Airtable-assigned id) plus the new one
 * — Airtable fetches the URL and re-hosts the file internally. Returns the updated Thread record.
 */
export async function uploadThreadAttachment(threadId, file, currentAttachments) {
  const token = await window.__clerkGetToken()
  const credRes = await fetch('/api/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'threadAttachmentUploadUrl', filename: file.name }),
  })
  if (!credRes.ok) {
    const b = await credRes.json().catch(() => ({}))
    throw new Error(b.error || ('Could not get upload URL (' + credRes.status + ')'))
  }
  const { signedUrl, publicUrl } = await credRes.json()

  const uploadRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => '')
    throw new Error('Storage upload failed (' + uploadRes.status + ')' + (detail ? ': ' + detail.slice(0, 200) : ''))
  }

  const nextAttachments = [...(currentAttachments || []), { url: publicUrl, filename: file.name }]
  return apiPatch(TBL_THREADS, threadId, { 'Attachments': nextAttachments })
}

/** Remove one attachment (by its Airtable attachment id) from a Thread's Attachments field. */
export function deleteThreadAttachment(threadId, attachmentId, currentAttachments) {
  const next = (currentAttachments || []).filter(a => a.id !== attachmentId)
  return apiPatch(TBL_THREADS, threadId, { 'Attachments': next })
}

/**
 * Fetch a single Thread record by id (raw Airtable record { id, fields }).
 * Used to render a CA item's linked Cortex thread inline.
 */
export function getThreadById(threadId) {
  if (!threadId) return Promise.resolve(null)
  // Single-record GET: must use apiGetOne (returns { id, fields } or null).
  // apiGet strips to `.records || []`, which a single-record response lacks → [].
  return apiGetOne(TBL_THREADS, threadId)
}

/**
 * Fetch all Thread Activities for a thread, sorted by Activity Date desc.
 * Uses {Record ID (from Threads)} lookup field to filter (note plural "Threads").
 */
export function getThreadActivities(threadId) {
  const formula = 'FIND("' + threadId + '",ARRAYJOIN({Record ID (from Threads)},","))'
  return apiGet(
    TBL_THREAD_ACT,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Activity+Date&sort%5B0%5D%5Bdirection%5D=desc&maxRecords=100'
  )
}

/**
 * Create a Thread Activity record.
 * Note: Thread Status is a formula field — closing is controlled by the 'Close Thread'
 * checkbox field on the Activity record, not by directly patching Thread.Status.
 *
 * @param {string} threadId — Thread record ID
 * @param {string} type — Activity Type singleSelect value
 * @param {string} date — ISO date string
 * @param {string|null} notes — Optional notes text
 * @param {string} memberId — Member record ID
 * @param {string|null} ballInCourt — Optional free-text ball-in-court note
 * @param {boolean} closeThread — Whether to set Close Thread checkbox
 */
export function createThreadActivity(threadId, type, date, notes, memberId, closeThread) {
  const fields = {
    'Thread':        [threadId],
    'Activity Type': type,
    'Activity Date': date,
  }
  if (memberId)    fields['Recorded by']   = [memberId]   // note: lowercase 'b' — matches Airtable field name exactly
  if (notes)       fields['Notes']         = notes
  if (closeThread) fields['Close Thread']  = true
  // Ball In Court is a multipleRecordLinks field — not writable from free-text input
  return apiPost(TBL_THREAD_ACT, fields)
}

/** Create many Thread Activity records at once, 10 per request, in order. Specs mirror
    createThreadActivity's arguments: { threadId, type, date, notes?, memberId?, closeThread? }. */
export function createThreadActivitiesBatch(specs) {
  const records = (specs || []).map(s => {
    const f = { 'Thread': [s.threadId], 'Activity Type': s.type, 'Activity Date': s.date }
    if (s.memberId)    f['Recorded by']  = [s.memberId]
    if (s.notes)       f['Notes']        = s.notes
    if (s.closeThread) f['Close Thread'] = true
    return f
  })
  if (!records.length) return Promise.resolve([])
  return apiPostBatch(TBL_THREAD_ACT, records).then(out => {
    // Hand-off followers hear about each thread once per batch (its last activity).
    const last = {}
    for (const sp of specs || []) last[sp.threadId] = sp
    for (const sp of Object.values(last)) notifyThreadFollowers(sp.threadId, sp.memberId || null, actorName(), sp.type, sp.notes || '')
    return out
  })
}

/** Patch a Thread Activity record (used to keep the 'Thread Opened' note synced with the thread Description). */
export function updateThreadActivity(activityId, fields) {
  return apiPatch(TBL_THREAD_ACT, activityId, fields)
}

/** Delete a single Thread Activity record (the row's ⋯ menu → Delete). No side effects on the Thread itself. */
export function deleteThreadActivity(activityId) {
  return apiDelete(TBL_THREAD_ACT, activityId)
}

/** Sync the thread's opening activity ('Thread Opened') note with the thread Description. Best-effort. */
export async function syncOpeningActivityNote(threadId, description) {
  try {
    const acts = await getThreadActivities(threadId)
    const opened = (acts || []).find(a => (a.fields && a.fields['Activity Type']) === 'Thread Opened')
    if (opened) await updateThreadActivity(opened.id, { 'Notes': (description || '').trim() })
  } catch (_) { /* non-fatal */ }
}

/* ── Thread lifecycle (Round 7) ─────────────────────────────────────────────
 * The Status formula on Threads reads the WRITABLE 'Closed' checkbox
 * (fldM0YPkcWKF7EVF7) — so 'Thread Closed' / 'Thread Reopened' activities flip
 * the thread directly here, instantly, instead of relying on a checkbox in the
 * activity modal + an Airtable automation. 'Last Activity' (writable date,
 * used for list sort) is touched on every logged activity.
 */

/**
 * Log a thread activity AND keep the thread record current.
 * - 'Thread Closed'   → activity carries Close Thread=true + thread Closed=true
 * - 'Item Completed'  → same: the thread's item is done, so the thread closes (Brandon's AT-6AV2G,
 *                       2026-10-26 — it used to only log, leaving the thread open)
 * - 'Thread Reopened' → thread Closed=false
 * - every type        → thread 'Last Activity' = activity date
 * The single entry point the activity modal and CA-item prompts use.
 */
/** Activity types that close the thread when logged. */
export const CLOSING_ACTIVITY_TYPES = ['Thread Closed', 'Item Completed']

export async function logThreadActivity(threadId, type, date, notes, memberId) {
  const isClose  = CLOSING_ACTIVITY_TYPES.includes(type)
  const isReopen = type === 'Thread Reopened'
  const res = await createThreadActivity(threadId, type, date, notes, memberId, isClose)
  if (res && res.error) return res
  const fields = { 'Last Activity': date }
  if (isClose)  fields['Closed'] = true
  if (isReopen) fields['Closed'] = false
  try { await updateThread(threadId, fields) } catch (_) { /* non-fatal — automation may still catch up */ }
  notifyThreadFollowers(threadId, memberId || null, actorName(), type, notes || '')
  return res
}

/** Record on a thread that something was added to the client's hub because of it (the morning
 *  check's "Add to Client Hub…", Matt 2026-10-15). An ordinary 'Action Taken' activity dated
 *  today in local time, so it reads in the thread's log like any hand-logged action. */
export function logClientHubAction(threadId, memberId, note) {
  const d = new Date()
  const local = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  return logThreadActivity(threadId, 'Action Taken', local, note, memberId)
}

/** Close a thread: 'Thread Closed' activity + Closed checkbox, dated today. */
export function closeThread(threadId, memberId, notes) {
  return logThreadActivity(threadId, 'Thread Closed', todayStr(), notes || null, memberId)
}

/**
 * FAST TRACK a thread to landing (Flight Plan, 2026-08-28) — the tower controller's
 * administrative close for work that is already done (or dead) in reality: no crew walks the
 * subtasks, no activity is typed in the moment. One call:
 *   1. every OPEN subtask (Thread Action Items) is completed through the real per-item path
 *      (toggleThreadActionItem — same Complete + Completed By attribution a hand-tick writes),
 *      closing the gap where closeThread() alone left subtasks dangling open forever;
 *   2. the thread closes via closeThread() — the ordinary 'Thread Closed' activity + Closed
 *      checkbox + Last Activity, so the activity record is complete, exactly as a worked close.
 * The note ALWAYS carries a "Fast-tracked" stamp (prepended if a custom note is given): a
 * couple hundred administrative clears must never read as organically-worked completions in
 * practice metrics / thread stats later. Deliberately sequential and non-atomic — Airtable has
 * no transactions; if the close itself fails after subtasks completed, the thread is simply a
 * still-open thread with a tidier subtask list, safe to retry.
 * Returns { subtasksCompleted } on success; throws on the close failing.
 */
export async function fastTrackThread(threadId, memberId, note) {
  const { getThreadActionItems, toggleThreadActionItem } = await import('./threadActionItems.js')
  let subtasksCompleted = 0
  try {
    const items = await getThreadActionItems(threadId)
    const open = (items || []).filter(i => (i.fields || {})['Complete'] !== true)
    for (const it of open) {
      await toggleThreadActionItem(it.id, true, memberId)
      subtasksCompleted++
    }
  } catch (_) {
    // Subtask completion is best-effort: a thread with an unreachable/failed subtask list can
    // still be closed — the close (below) is the part that must not silently no-op.
  }
  const stamp = 'Fast-tracked' + (note && note.trim() ? ' — ' + note.trim() : '')
  const res = await closeThread(threadId, memberId, stamp)
  if (res && res.error) throw new Error(typeof res.error === 'string' ? res.error : 'Close failed')
  return { subtasksCompleted }
}

/** Reopen a thread: 'Thread Reopened' activity + Closed unchecked, dated today. */
export function reopenThread(threadId, memberId, notes) {
  return logThreadActivity(threadId, 'Thread Reopened', todayStr(), notes || null, memberId)
}

/**
 * The project's Construction Support phase record ID (name match), or null.
 * RFI/Submittal auto-created threads attach here so they mount under
 * Schedule > Construction Support > Threads.
 */
export function constructionSupportPhaseId(phases) {
  const ph = (phases || []).find(p => ((p.fields && p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1)
  return ph ? ph.id : null
}
